import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { Copy16 } from '@makyoapp/makyo-icon/icon'
import {
  Button,
  ButtonColor,
  ButtonVariant,
  DeleteModal,
  JustifyVariant,
  Loading,
  Modal,
  SimpleInput,
} from '@makyoapp/makyo-ui'

import { useTwoFaContext } from '@/context/TwoFaContext'
import { Info } from '@/components/Info'
import { SaveButton } from '@/components/SaveButton'

import use2FAEnabled from '../hooks/use2FAEnabled'
import useSetup2FA from '../hooks/useSetup2FA'

function cleanOtp(otp: string) {
  return otp.trim().replaceAll(/\D/g, '').substring(0, 6)
}

/**
 * 2FA Setup View
 *
 * show only the input without reset 2 point of setup
 * @param onlyInput
 * @constructor
 */
function Setup2FA({ onlyInput }: { onlyInput?: boolean }) {
  const {
    qrUrl,
    setClipBoard,
    value,
    twoFactorOtp,
    setTwoFactorOtp,
    handleSubmit,
    isBtnLoading,
    errOtp,
    hasOtpError,
    setValue,
    t,
  } = useSetup2FA({ onlyInput })

  return (
    <>
      {!onlyInput && (
        <>
          {/* Header title */}
          <h3 className='mb-4'>{t('Setup Two-Factor Authentication')}</h3>
          {/* Step 1 title */}
          <h4>1. {t('Install a 2-Factor Authentication App')}</h4>
          {/* Step 1 description with recommendation links */}
          <p className='pb-4'>
            <Trans i18nKey='2faStep1Info'>
              We recommend{' '}
              <a
                className='link'
                href='https://play.google.com/store/apps/details?id=com.azure.authenticator'
                target='_blank'
                rel='noreferrer'
              >
                Microsoft Authenticator
              </a>{' '}
              or{' '}
              <a
                className='link'
                href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
                target='_blank'
                rel='noreferrer'
              >
                Google Authenticator
              </a>
              .
            </Trans>
          </p>
          {/* Step 2 title */}
          <h4>2. {t('Configure the Application')}</h4>
          {/* Step 2 description with QR code */}
          <div className='pb-4'>
            <Trans i18nKey='2faStep2Info'>
              Using your two-factor app, <strong>scan the QR code</strong> or manually{' '}
              <strong>enter the secret key</strong> to add your Makyo account to the app.
            </Trans>
            {/* QR code image */}
            <img
              alt='QR'
              width={150}
              src={qrUrl}
              onLoad={() => {
                URL.revokeObjectURL(qrUrl)
              }}
            />
            <div className='flex items-center'>
              {/* Secret key label */}
              <strong>Secret Key:</strong>
              {/* Button to copy secret key */}
              <Button
                id='2fa-copy-btn'
                onClick={() => {
                  setClipBoard(value.secret)
                }}
                variant={ButtonVariant.Ghost}
                icon={<Copy16 />}
                noPadding
                className='w-9'
              />
            </div>
            {/* Secret key value */}
            <div>{value.secret}</div>
          </div>
        </>
      )}
      {/* Step 3 title */}
      <h4>
        {onlyInput ? '' : '3. '}
        {t('Enter the App Generated 6-Digit Code')}
      </h4>
      {/* Step 3 description */}
      <p>
        <Trans i18nKey='2faStep3Info'>
          Once your Makyo account is successfully added to the app, a 6-digit code will appear. Enter that code below.
        </Trans>
      </p>
      {/* Input field for 6-digit code */}
      <div className='not-prose flex items-start gap-2 grow-0 mb-4'>
        <div>
          <SimpleInput
            id='setup-2fa-otp-input'
            value={twoFactorOtp}
            className='w-[110px]'
            placeholder={t('6-digit code')}
            onChange={(e) => {
              const val = cleanOtp(e.currentTarget.value)
              setTwoFactorOtp(val)
            }}
            error={errOtp}
            onKeyUp={(e) => {
              if (e.key === 'Enter' && twoFactorOtp.length === 6) {
                handleSubmit()
              }
            }}
          />
        </div>
        {/* Enable button */}
        <div className='flex-c4'>
          <SaveButton
            id='submit-2fa-otp-btn'
            isLoading={isBtnLoading}
            disabled={isBtnLoading || hasOtpError}
            onClick={handleSubmit}
            label={t('Enable')}
          />
          {/* Cancel button */}
          <button
            id='cancel-2fa-setup-btn'
            className='link underline'
            onClick={() => {
              setValue({
                showSetup: false,
              })
            }}
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    </>
  )
}

const TwoFaResetButton = () => {
  const { t } = useTranslation()
  const { handleReset } = useTwoFaContext()
  const [showReset, setShowReset] = useBoolean(false)
  const [resetOtp, setResetOtp] = useState('')
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)
  const [errOtp, setErrOtp] = useState('')

  const handleSubmit = () => {
    setIsBtnLoading(true)
    handleReset(resetOtp, {
      onSuccess: () => setShowReset(false),
      onFailed: () => setErrOtp(t('Invalid code')),
      onFinally: () => setIsBtnLoading(false),
    })
  }

  return (
    <>
      {/* Information about resetting secret key */}
      <p>
        <Trans i18nKey='2faResetInfo'>To change authentication apps, you must reset your secret key.</Trans>
      </p>
      <div className='not-prose pb-2'>
        {/* Button to reset 2FA */}
        <Button
          id='reset-2fa-btn'
          variant={ButtonVariant.Outlined}
          className='flex-cc w-40'
          colorStyle={ButtonColor.Danger}
          onClick={() => setShowReset(true)}
        >
          {t('Reset 2FA')}
        </Button>
        {/* Modal for resetting 2FA */}
        <DeleteModal
          id='reset-2fa-modal'
          isLoading={isBtnLoading}
          show={showReset}
          onHide={() => setShowReset(false)}
          onSubmit={handleSubmit}
          continueLabel={t('Yes, reset')}
          canContinue={resetOtp.length === 6}
          title={t('Reset 2FA Secret Key')}
          withCancel
        >
          <div className='modal-sm'>
            {/* Confirmation message */}
            <div className='text-base mb-2'>{t('Are you sure you want to reset your secret key?')}</div>
            {/* Warning about resetting */}
            <Info gap='long' className='p-4 border border-red-600 dark:border-red-400 rounded-sm mb-2' isDanger={true}>
              {t('Your current secret key will be disabled and you will need to repeat the setup process.')}
            </Info>
            {/* Input field for 6-digit code */}
            <div className='flex-c w-[120px]'>
              <SimpleInput
                id='reset-2fa-otp-input'
                value={resetOtp}
                placeholder={t('6-digit code')}
                error={errOtp}
                onChange={(e) => {
                  const val = cleanOtp(e.currentTarget.value)
                  setResetOtp(val)
                }}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' && resetOtp.length === 6) {
                    handleSubmit()
                  }
                }}
              />
            </div>
          </div>
        </DeleteModal>
      </div>
    </>
  )
}

const TwoFaEnabled = () => {
  const { showDisable2Fa, setShowDisable2Fa, isBtnLoading, handleSubmit, disableOtp, setDisableOtp, errOtp, t } =
    use2FAEnabled()

  return (
    <>
      {/* Information about 2FA status */}
      <Info className='pb-4' defaultTextStyle={false}>
        <Trans i18nKey='2faEnabled'>
          Two-factor Authentication is <strong className='uppercase'>enabled</strong>
        </Trans>
      </Info>
      <div className='not-prose pb-4'>
        {/* Button to disable 2FA */}
        <Button
          id='disable-2fa-btn'
          className='flex-cc w-40'
          onClick={() => setShowDisable2Fa(true)}
          colorStyle={ButtonColor.Danger}
        >
          {t('Disable 2FA')}
        </Button>
        {/* Modal for disabling 2FA */}
        <DeleteModal
          id={'disable-2fa-modal'}
          show={showDisable2Fa}
          isLoading={isBtnLoading}
          onHide={() => setShowDisable2Fa(false)}
          onSubmit={handleSubmit}
          canContinue={disableOtp.length === 6}
          continueLabel={t('Yes, disable')}
          title={t('Disable Two-Factor Authentication')}
          withCancel
        >
          <div className='modal-sm'>
            {/* Confirmation message */}
            <div className='text-base mb-2'>{t('Are you sure you want to disable your 2FA?')}</div>
            {/* Description of what disabling 2FA means */}
            <div className='text-m-m text-light mb-2'>
              <Trans i18nKey='disable2FaDetail'>
                This means, you will not be required to enter the 2FA code next time you login to Makyo
              </Trans>
            </div>
            {/* Input field for 6-digit code */}
            <div className='flex-c w-[120px]'>
              <SimpleInput
                id='disable-2fa-otp-input'
                value={disableOtp}
                placeholder={t('6-digit code')}
                onChange={(e) => {
                  const val = cleanOtp(e.currentTarget.value)
                  setDisableOtp(val)
                }}
                error={errOtp}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' && disableOtp.length === 6) {
                    handleSubmit()
                  }
                }}
              />
            </div>
          </div>
        </DeleteModal>
      </div>
      {/* Button for resetting 2FA */}
      <TwoFaResetButton />
    </>
  )
}

const TwoFaDisabled = () => {
  const { t } = useTranslation()
  const { value, setValue, isLoading, handleGetQr } = useTwoFaContext()

  const handleSaveButton = () => {
    // Check if 2FA needs setup
    if (value.needSetup) {
      // Check if QR code and secret key exist
      if (!value.qr || !value.secret) {
        // Get QR code and then show the setup
        handleGetQr(() => {
          setValue({ showSetup: true })
        })
      } else {
        // Show the setup
        setValue({ showSetup: true })
      }
    } else {
      // Show the setup
      setValue({ showSetup: true })
    }
  }

  // show setup only when needed
  return !value.showSetup ? (
    <>
      {/* Information about 2FA status */}
      <Info className='pb-4' defaultTextStyle={false}>
        <Trans i18nKey='2faDisabled'>
          Two-factor Authentication is <strong className='uppercase red'>disabled</strong>
        </Trans>
      </Info>
      <div className='not-prose pb-4'>
        {/* Button to enable 2FA */}
        <SaveButton
          id='enable-2fa-btn'
          isLoading={isLoading}
          className='flex-cc w-40'
          onClick={handleSaveButton}
          label={value.needSetup ? t('Setup & Enable') : t('Enable')}
        />
      </div>
      {/* If 2FA does not need setup, show the reset button */}
      {!value.needSetup && <TwoFaResetButton />}
    </>
  ) : (
    <Setup2FA onlyInput={!value.needSetup} />
  )
}

/**
 * 2FA Tab Panel
 *
 * need to be wrap in `<TwoFaProvider />`
 */
function Panel2Fa() {
  const { t } = useTranslation()
  const { value, setValue, isLoading, showResetSuccess, setShowResetSuccess, handleGetQr } = useTwoFaContext()

  const textContent = t('backup2FaTextFile')
  const scratchBlob = new Blob([[textContent, '', value.scratch.join('\n')].join('\n')], {
    type: 'text/plain',
  })

  const scratchUrl = URL.createObjectURL(scratchBlob)
  const showBackup = value.scratch.length > 0 && value.showBackup

  const handleHide = useCallback(() => {
    setValue({
      showBackup: false,
    })
  }, [setValue])

  return isLoading ? (
    <Loading />
  ) : (
    <div className='prose dark:prose-invert prose-sm'>
      {/* If 2FA setup is not being shown, display the 2FA info */}
      {!value.showSetup && (
        <>
          <h3 className='mb-4'>{t('Two-Factor Authentication')}</h3>
          <p className='pb-4'>
            <Trans i18nKey='2faInfo'>
              Two-factor authentication adds an extra layer of protection to logins. Once enabled and configured, each
              time you sign in you will be asked to enter your email, OTP-code and a two-factor code.
            </Trans>
          </p>
        </>
      )}
      {/* Depending on whether 2FA is enabled or not, display the appropriate component */}
      {value.enabled ? <TwoFaEnabled /> : <TwoFaDisabled />}
      {/* If backup codes should be shown, display a modal with the backup codes */}
      {showBackup && (
        <Modal id='2fa-backup-modal' show={value.scratch.length > 0} onHide={handleHide}>
          <Modal.Header onCloseClick={handleHide}>{t('Download backup code')}</Modal.Header>
          <Modal.Body>
            <div className='not-prose modal-sm'>
              <Info className='mb-4'>
                {/* Inform the user about the importance of the backup codes */}
                <Trans i18nKey='downloadScratchCode'>
                  Download this backup code and keep it somewhere safe. If you ever lose access to your authenticator
                  app, use one of the backup codes instead. <strong>this button only appear once!</strong>
                </Trans>
              </Info>
            </div>
          </Modal.Body>
          {/* The button to download the backup codes */}
          <Modal.Footer justify={JustifyVariant.End}>
            <a
              className='btn'
              download='makyo-2fa-backup.txt'
              href={scratchUrl}
              onClick={() => {
                setValue({
                  showBackup: false,
                })
              }}
            >
              {t('Download backup code')}
            </a>
          </Modal.Footer>
        </Modal>
      )}
      {/* If 2FA reset was successful, show a success modal */}
      {showResetSuccess && (
        <Modal id='reset-success-2fa-modal' show={true} onHide={() => setShowResetSuccess(false)}>
          <Modal.Header onCloseClick={() => setShowResetSuccess(false)}>{t('2FA Reset Successfully!')}</Modal.Header>
          <Modal.Body>
            <div className='text-base modal-sm'>{t('What would you like to do next?')}</div>
          </Modal.Body>
          <Modal.Footer className='justify-between'>
            {/* Button to return to the account page */}
            <Button
              id='reset-success-2fa-modal-back'
              variant={ButtonVariant.Outlined}
              onClick={() => setShowResetSuccess(false)}
            >
              {t('Back to My Account')}
            </Button>
            {/* Button to re-enable 2FA */}
            <SaveButton
              id='reset-success-2fa-modal-enable'
              isLoading={isLoading}
              onClick={() => {
                handleGetQr(() => {
                  setValue({
                    showSetup: true,
                  })
                  setShowResetSuccess(false)
                })
              }}
              label={t('Re-enable 2FA')}
            />
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

export default Panel2Fa
